import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Sparkles,
  Users,
  Settings,
  LogOut,
  Palette,
  FileText,
  Shield,
  X,
  Check,
  Trash2,
  CreditCard,
  Globe,
  Plus,
  Edit,
  Key
} from 'lucide-react';
import { getTranslation } from '../translations';
import './AdminPanel.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminPanel({ user, onLogout, theme, onThemeChange, language, onLanguageChange }) {
  const t = (key) => getTranslation(key, language);
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [promptTypes, setPromptTypes] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [editingType, setEditingType] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [siteForm, setSiteForm] = useState({});
  
  // New state for user management
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    subscription_tier: 'free'
  });
  const [editUserForm, setEditUserForm] = useState({});
  const [passwordForm, setPasswordForm] = useState({ new_password: '' });
  
  // New state for prompt type management
  const [showCreatePromptTypeModal, setShowCreatePromptTypeModal] = useState(false);
  const [newPromptTypeForm, setNewPromptTypeForm] = useState({
    type_key: '',
    title: '',
    subtitle: '',
    description: '',
    system_instructions: ''
  });

  useEffect(() => {
    loadUsers();
    loadPromptTypes();
    loadSiteSettings();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadPromptTypes = async () => {
    try {
      const response = await axios.get(`${API}/prompt-types`);
      setPromptTypes(response.data);
    } catch (error) {
      console.error('Error loading prompt types:', error);
    }
  };

  const loadSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      setSiteSettings(response.data);
      setSiteForm(response.data);
    } catch (error) {
      console.error('Error loading site settings:', error);
    }
  };

  const handleUpdateUserStatus = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { status });
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleEditPromptType = (type) => {
    setEditingType(type.type_key);
    setEditForm({
      title: type.title,
      subtitle: type.subtitle || '',
      description: type.description,
      system_instructions: type.system_instructions
    });
  };

  const handleSavePromptType = async () => {
    try {
      await axios.put(`${API}/admin/prompt-types/${editingType}`, editForm);
      setEditingType(null);
      loadPromptTypes();
    } catch (error) {
      console.error('Error updating prompt type:', error);
    }
  };

  // New user management handlers
  const handleCreateUser = async () => {
    try {
      await axios.post(`${API}/admin/users`, newUserForm);
      setShowCreateUserModal(false);
      setNewUserForm({
        name: '',
        email: '',
        password: '',
        role: 'user',
        subscription_tier: 'free'
      });
      loadUsers();
      alert('User created successfully!');
    } catch (error) {
      console.error('Error creating user:', error);
      alert(error.response?.data?.detail || 'Error creating user');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserForm({
      name: user.name,
      email: user.email,
      role: user.role,
      subscription_tier: user.subscription_tier,
      subscription_status: user.subscription_status
    });
    setShowEditUserModal(true);
  };

  const handleSaveEditUser = async () => {
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/edit`, editUserForm);
      setShowEditUserModal(false);
      setSelectedUser(null);
      loadUsers();
      alert('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      alert(error.response?.data?.detail || 'Error updating user');
    }
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setPasswordForm({ new_password: '' });
    setShowPasswordModal(true);
  };

  const handleSavePassword = async () => {
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/password`, passwordForm);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setPasswordForm({ new_password: '' });
      alert('Password updated successfully!');
    } catch (error) {
      console.error('Error updating password:', error);
      alert(error.response?.data?.detail || 'Error updating password');
    }
  };

  const handleChangeSubscription = async (userId, newPlan) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/subscription`, { plan: newPlan });
      loadUsers();
      alert('Subscription updated successfully!');
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert(error.response?.data?.detail || 'Error updating subscription');
    }
  };

  // New prompt type handlers
  const handleCreatePromptType = async () => {
    try {
      await axios.post(`${API}/admin/prompt-types`, newPromptTypeForm);
      setShowCreatePromptTypeModal(false);
      setNewPromptTypeForm({
        type_key: '',
        title: '',
        subtitle: '',
        description: '',
        system_instructions: ''
      });
      loadPromptTypes();
      alert('Prompt type created successfully!');
    } catch (error) {
      console.error('Error creating prompt type:', error);
      alert(error.response?.data?.detail || 'Error creating prompt type');
    }
  };

  const handleDeletePromptType = async (typeKey) => {
    if (!window.confirm('Are you sure you want to delete this prompt type?')) return;

    try {
      await axios.delete(`${API}/admin/prompt-types/${typeKey}`);
      loadPromptTypes();
      alert('Prompt type deleted successfully!');
    } catch (error) {
      console.error('Error deleting prompt type:', error);
      alert(error.response?.data?.detail || 'Error deleting prompt type');
    }
  };

  const handleSaveSiteSettings = async () => {
    try {
      await axios.put(`${API}/admin/site-settings`, siteForm);
      loadSiteSettings();
      alert('Site settings updated successfully!');
    } catch (error) {
      console.error('Error updating site settings:', error);
    }
  };

  return (
    <div className="admin-panel" data-testid="admin-panel">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-header">
          <div className="admin-logo">
            <Sparkles size={24} />
            <h2>{siteSettings?.site_title || 'Promptify Master'}</h2>
          </div>
          <span className="admin-badge">Admin</span>
        </div>

        <nav className="admin-nav">
          <button
            className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            data-testid="nav-users"
          >
            <Users size={18} />
            User Management
          </button>
          <button
            className={`nav-item ${activeTab === 'prompts' ? 'active' : ''}`}
            onClick={() => setActiveTab('prompts')}
            data-testid="nav-prompts"
          >
            <FileText size={18} />
            Prompt Types
          </button>
          <button
            className={`nav-item ${activeTab === 'site' ? 'active' : ''}`}
            onClick={() => setActiveTab('site')}
            data-testid="nav-site"
          >
            <Globe size={18} />
            Site Settings
          </button>
          <button
            className={`nav-item ${activeTab === 'themes' ? 'active' : ''}`}
            onClick={() => setActiveTab('themes')}
            data-testid="nav-themes"
          >
            <Palette size={18} />
            Theme Settings
          </button>
        </nav>

        <div className="admin-footer">
          <a href="/dashboard" className="footer-link">
            <Shield size={18} />
            Back to Dashboard
          </a>
          <button className="footer-link" onClick={onLogout}>
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-content">
          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="tab-content fade-in">
              <div className="content-header">
                <h1>User Management</h1>
                <p>Manage user accounts and subscriptions</p>
                <button 
                  className="action-btn create"
                  onClick={() => setShowCreateUserModal(true)}
                  style={{ marginTop: '10px' }}
                >
                  <Plus size={16} /> Create User
                </button>
              </div>

              <div className="users-table-container">
                <table className="users-table" data-testid="users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Subscription</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.name}</td>
                        <td>{u.email}</td>
                        <td>
                          <span className={`role-badge ${u.role}`}>{u.role}</span>
                        </td>
                        <td>
                          <select
                            className="subscription-select"
                            value={u.subscription_tier}
                            onChange={(e) => handleChangeSubscription(u.id, e.target.value)}
                            disabled={u.role === 'admin'}
                          >
                            <option value="free">Free</option>
                            <option value="standard">Standard</option>
                            <option value="premium">Premium</option>
                          </select>
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              u.subscription_status === 'active' ? 'active' : 'inactive'
                            }`}
                          >
                            {u.subscription_status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="action-btn edit"
                              onClick={() => handleEditUser(u)}
                              title="Edit User"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              className="action-btn password"
                              onClick={() => handleChangePassword(u)}
                              title="Change Password"
                            >
                              <Key size={14} />
                            </button>
                            {u.subscription_status === 'active' ? (
                              <button
                                className="action-btn disable"
                                onClick={() =>
                                  handleUpdateUserStatus(u.id, 'disabled')
                                }
                                data-testid={`disable-user-${u.id}`}
                              >
                                Disable
                              </button>
                            ) : (
                              <button
                                className="action-btn enable"
                                onClick={() => handleUpdateUserStatus(u.id, 'active')}
                                data-testid={`enable-user-${u.id}`}
                              >
                                Enable
                              </button>
                            )}
                            {u.role !== 'admin' && (
                              <button
                                className="action-btn delete"
                                onClick={() => handleDeleteUser(u.id)}
                                data-testid={`delete-user-${u.id}`}
                                title="Delete User"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Prompt Types Tab */}
          {activeTab === 'prompts' && (
            <div className="tab-content fade-in">
              <div className="content-header">
                <h1>Prompt Types</h1>
                <p>Create, edit, and delete prompt types</p>
                <button 
                  className="action-btn create"
                  onClick={() => setShowCreatePromptTypeModal(true)}
                  style={{ marginTop: '10px' }}
                >
                  <Plus size={16} /> Create Prompt Type
                </button>
              </div>

              <div className="prompt-types-grid">
                {promptTypes.map((type) => (
                  <div key={type.type_key} className="prompt-type-card">
                    {editingType === type.type_key ? (
                      <div className="edit-form">
                        <div className="form-group">
                          <label>Title</label>
                          <input
                            type="text"
                            className="edit-input"
                            value={editForm.title}
                            onChange={(e) =>
                              setEditForm({ ...editForm, title: e.target.value })
                            }
                            data-testid={`edit-title-${type.type_key}`}
                            placeholder="Enter title"
                          />
                        </div>
                        <div className="form-group">
                          <label>Subtitle</label>
                          <input
                            type="text"
                            className="edit-input"
                            value={editForm.subtitle}
                            onChange={(e) =>
                              setEditForm({ ...editForm, subtitle: e.target.value })
                            }
                            data-testid={`edit-subtitle-${type.type_key}`}
                            placeholder="Enter subtitle (optional)"
                          />
                        </div>
                        <div className="form-group">
                          <label>Description</label>
                          <textarea
                            className="edit-textarea"
                            value={editForm.description}
                            onChange={(e) =>
                              setEditForm({ ...editForm, description: e.target.value })
                            }
                            data-testid={`edit-description-${type.type_key}`}
                            placeholder="Enter description"
                          />
                        </div>
                        <div className="form-group">
                          <label>System Instructions</label>
                          <textarea
                            className="edit-textarea large"
                            value={editForm.system_instructions}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                system_instructions: e.target.value
                              })
                            }
                            placeholder="System instructions..."
                            data-testid={`edit-instructions-${type.type_key}`}
                          />
                        </div>
                        <div className="edit-actions">
                          <button
                            className="save-btn"
                            onClick={handleSavePromptType}
                            data-testid={`save-${type.type_key}`}
                          >
                            <Check size={16} />
                            Save
                          </button>
                          <button
                            className="cancel-btn"
                            onClick={() => setEditingType(null)}
                            data-testid={`cancel-${type.type_key}`}
                          >
                            <X size={16} />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="type-header">
                          <div>
                            <h3>{type.title}</h3>
                            {type.subtitle && <p className="type-subtitle">{type.subtitle}</p>}
                          </div>
                          <span className="type-key">{type.type_key}</span>
                        </div>
                        <p className="type-description">{type.description}</p>
                        <p className="type-instructions">{type.system_instructions}</p>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                          <button
                            className="edit-btn"
                            onClick={() => handleEditPromptType(type)}
                            data-testid={`edit-${type.type_key}`}
                          >
                            <Settings size={16} />
                            Edit
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeletePromptType(type.type_key)}
                            data-testid={`delete-${type.type_key}`}
                          >
                            <Trash2 size={16} />
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Site Settings Tab */}
          {activeTab === 'site' && (
            <div className="tab-content fade-in">
              <div className="content-header">
                <h1>Site Settings</h1>
                <p>Edit site title and landing page content</p>
              </div>

              <div className="site-settings-form">
                <div className="form-section">
                  <h3>General Settings</h3>
                  <div className="form-group">
                    <label>Site Title</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={siteForm.site_title || ''}
                      onChange={(e) =>
                        setSiteForm({ ...siteForm, site_title: e.target.value })
                      }
                      placeholder="Promptify Master"
                    />
                  </div>
                  <div className="form-group">
                    <label>Site Description</label>
                    <textarea
                      className="edit-textarea"
                      value={siteForm.site_description || ''}
                      onChange={(e) =>
                        setSiteForm({ ...siteForm, site_description: e.target.value })
                      }
                      placeholder="Transform your lazy prompts into professional masterpieces"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3>Landing Page Content</h3>
                  <div className="form-group">
                    <label>Landing Title</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={siteForm.landing_title || ''}
                      onChange={(e) =>
                        setSiteForm({ ...siteForm, landing_title: e.target.value })
                      }
                      placeholder="Turn your lazy prompts into great ones"
                    />
                  </div>
                  <div className="form-group">
                    <label>Landing Subtitle</label>
                    <textarea
                      className="edit-textarea"
                      value={siteForm.landing_subtitle || ''}
                      onChange={(e) =>
                        setSiteForm({ ...siteForm, landing_subtitle: e.target.value })
                      }
                      placeholder="Get professional prompt results in seconds with AI-powered improvement"
                    />
                  </div>
                </div>

                <button className="save-btn large" onClick={handleSaveSiteSettings}>
                  <Check size={18} />
                  Save All Changes
                </button>
              </div>
            </div>
          )}

          {/* Themes Tab */}
          {activeTab === 'themes' && (
            <div className="tab-content fade-in">
              <div className="content-header">
                <h1>Theme Settings</h1>
                <p>Choose the application theme</p>
              </div>

              <div className="theme-options">
                <button
                  className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                  onClick={() => onThemeChange('dark')}
                  data-testid="admin-theme-dark"
                >
                  <div className="theme-preview dark-preview"></div>
                  <div className="theme-info">
                    <h3>Dark Theme</h3>
                    <p>Modern dark interface with cyan accents</p>
                  </div>
                  {theme === 'dark' && <Check size={20} />}
                </button>

                <button
                  className={`theme-option ${theme === 'white' ? 'active' : ''}`}
                  onClick={() => onThemeChange('white')}
                  data-testid="admin-theme-white"
                >
                  <div className="theme-preview white-preview"></div>
                  <div className="theme-info">
                    <h3>White Theme</h3>
                    <p>Clean light interface with blue accents</p>
                  </div>
                  {theme === 'white' && <Check size={20} />}
                </button>

                <button
                  className={`theme-option ${theme === 'terminal' ? 'active' : ''}`}
                  onClick={() => onThemeChange('terminal')}
                  data-testid="admin-theme-terminal"
                >
                  <div className="theme-preview terminal-preview"></div>
                  <div className="theme-info">
                    <h3>Terminal Theme</h3>
                    <p>Classic terminal with green text on black</p>
                  </div>
                  {theme === 'terminal' && <Check size={20} />}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="modal-overlay" onClick={() => setShowCreateUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New User</h2>
              <button onClick={() => setShowCreateUserModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="edit-input"
                  value={newUserForm.name}
                  onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                  placeholder="Enter name"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="edit-input"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                  placeholder="Enter email"
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  className="edit-input"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                  placeholder="Enter password"
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  className="edit-input"
                  value={newUserForm.role}
                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Subscription Tier</label>
                <select
                  className="edit-input"
                  value={newUserForm.subscription_tier}
                  onChange={(e) => setNewUserForm({ ...newUserForm, subscription_tier: e.target.value })}
                >
                  <option value="free">Free</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowCreateUserModal(false)}>
                Cancel
              </button>
              <button className="save-btn" onClick={handleCreateUser}>
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEditUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button onClick={() => setShowEditUserModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="edit-input"
                  value={editUserForm.name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="edit-input"
                  value={editUserForm.email}
                  onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  className="edit-input"
                  value={editUserForm.role}
                  onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Subscription Tier</label>
                <select
                  className="edit-input"
                  value={editUserForm.subscription_tier}
                  onChange={(e) => setEditUserForm({ ...editUserForm, subscription_tier: e.target.value })}
                >
                  <option value="free">Free</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  className="edit-input"
                  value={editUserForm.subscription_status}
                  onChange={(e) => setEditUserForm({ ...editUserForm, subscription_status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowEditUserModal(false)}>
                Cancel
              </button>
              <button className="save-btn" onClick={handleSaveEditUser}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Password for {selectedUser.name}</h2>
              <button onClick={() => setShowPasswordModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  className="edit-input"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ new_password: e.target.value })}
                  placeholder="Enter new password"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowPasswordModal(false)}>
                Cancel
              </button>
              <button className="save-btn" onClick={handleSavePassword}>
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Prompt Type Modal */}
      {showCreatePromptTypeModal && (
        <div className="modal-overlay" onClick={() => setShowCreatePromptTypeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Prompt Type</h2>
              <button onClick={() => setShowCreatePromptTypeModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Type Key (unique identifier)</label>
                <input
                  type="text"
                  className="edit-input"
                  value={newPromptTypeForm.type_key}
                  onChange={(e) => setNewPromptTypeForm({ ...newPromptTypeForm, type_key: e.target.value })}
                  placeholder="e.g., custom_type"
                />
              </div>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  className="edit-input"
                  value={newPromptTypeForm.title}
                  onChange={(e) => setNewPromptTypeForm({ ...newPromptTypeForm, title: e.target.value })}
                  placeholder="Enter title"
                />
              </div>
              <div className="form-group">
                <label>Subtitle (optional)</label>
                <input
                  type="text"
                  className="edit-input"
                  value={newPromptTypeForm.subtitle}
                  onChange={(e) => setNewPromptTypeForm({ ...newPromptTypeForm, subtitle: e.target.value })}
                  placeholder="Enter subtitle"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="edit-textarea"
                  value={newPromptTypeForm.description}
                  onChange={(e) => setNewPromptTypeForm({ ...newPromptTypeForm, description: e.target.value })}
                  placeholder="Enter description"
                />
              </div>
              <div className="form-group">
                <label>System Instructions</label>
                <textarea
                  className="edit-textarea"
                  value={newPromptTypeForm.system_instructions}
                  onChange={(e) => setNewPromptTypeForm({ ...newPromptTypeForm, system_instructions: e.target.value })}
                  placeholder="Enter system instructions for the LLM"
                  rows={6}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowCreatePromptTypeModal(false)}>
                Cancel
              </button>
              <button className="save-btn" onClick={handleCreatePromptType}>
                Create Prompt Type
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;